<template>
    <section class="doc-section">

        <!-- Selector de Plataforma -->
        <div class="mb-6">
            <BotonesNavegacion @cambio-plataforma="plataformaActual = $event" />
        </div>

        <header class="header-container">
            <h1>Colores por Estado del Dispositivo</h1>
            <p class="description">
                Vista que permite identificar visualmente el estado de los dispositivos mediante
                colores representados dentro del mapa de monitoreo.
            </p>
        </header>

        <div class="two-col">
            <div class="col-steps">
                <p class="steps-label">Identificación en Mapa</p>

                <div class="step-card">
                    <div class="step-badge">1</div>
                    <div>
                        <div class="step-title">Localizar dispositivos</div>
                        <div class="step-desc">
                            En la vista de monitoreo se visualizan todos los dispositivos registrados representados por iconos de colores.
                        </div>
                    </div>
                </div>

                <div class="step-card">
                    <div class="step-badge">2</div>
                    <div>
                        <div class="step-title">Interpretación de estados</div>
                        <div class="step-desc">
                            Cada icono mostrará un color específico que indica su condición operativa en tiempo real.
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-image">
                <transition name="fade" mode="out-in">
                    <CarruselAciqui
                        :key="plataformaActual"
                        :items="imagenesMapaMostrar"
                    />
                </transition>
            </div>
        </div>

        <div class="section-title-bar mb-4">
            <h2>Leyenda de Estados</h2>
        </div>

        <p class="main-desc">Utilice la leyenda visual para identificar rápidamente el estado operativo de cada unidad:</p>

        <div class="two-col alternate-flow">
            <div class="col-image">
                <transition name="fade" mode="out-in">
                    <CarruselAciqui
                        :key="plataformaActual"
                        :items="imagenesLeyendaMostrar"
                    />
                </transition>
            </div>

            <div class="col-steps">
                <p class="steps-label">Interpretación Visual</p>
                
                <div class="form-grid single-col">
                    <div class="field-row">
                        <span class="field-key">•</span>
                        <div>
                            <div class="field-label">Referencia de Colores</div>
                            <div class="field-desc">Identifique el significado de cada tono presente en el mapa.</div>
                        </div>
                    </div>
                    <div class="field-row">
                        <span class="field-key">•</span>
                        <div>
                            <div class="field-label">Soporte Operativo</div>
                            <div class="field-desc">La leyenda funciona como guía rápida de referencia dentro del monitoreo.</div>
                        </div>
                    </div>
                    <div class="field-row">
                        <span class="field-key">•</span>
                        <div>
                            <div class="field-label">Dinamicidad</div>
                            <div class="field-desc">Los estados cambian automáticamente según el reporte de la unidad.</div>
                        </div>
                    </div>
                </div>

                <div class="doc-note note-info">
                    <svg class="note-icon" width="15" height="15" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M8 5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <circle cx="8" cy="11.5" r="0.75" fill="currentColor"/>
                    </svg>
                    <div class="note-body">
                        <span class="note-label">Nota de configuración</span>
                        <p>Los colores y estados visualizados dependerán de la configuración específica y la información recibida por el sistema.</p>
                    </div>
                </div>
            </div>
        </div>

    </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import CarruselAciqui from '../Carruselimg.vue';
import BotonesNavegacion from '../BotonesNavegacion.vue';

const plataformaActual = ref('web');

/* ── Datos Web ───────────────────────────────────── */
const webData = {
    mapa: [
        {
            nombre: "Dispositivos en Mapa",
            url: '/img/datosDispositivos/Dispositivos_Mapa.png',
            descripcion: "Visualización de iconos de colores según el estado actual."
        }
    ],

    leyenda: [
        {
            nombre: "Leyenda de Estados",
            url: '/img/datosDispositivos/Leyenda_Colores_Estados.png',
            descripcion: "Cuadro de referencia visual para los estados operativos."
        }
    ]
};

/* ── Datos Móvil ─────────────────────────────────── */
/* Solo cambia la imagen del mapa */
const mobileData = {
    mapa: [
        {
            nombre: "Dispositivos en Mapa",
            url: '/img/datosDispositivos/Visualizar_Dispositivos_Mapa_Movil.png',
            descripcion: "Visualización de iconos de colores según el estado actual."
        }
    ],

    leyenda: [
        {
            nombre: "Leyenda de Estados",
            url: '/img/datosDispositivos/Leyenda_Colores_Estados.png',
            descripcion: "Cuadro de referencia visual para los estados operativos."
        }
    ]
};

/* ── Computed ────────────────────────────────────── */
const imagenesMapaMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.mapa
        : mobileData.mapa
);

const imagenesLeyendaMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.leyenda
        : mobileData.leyenda
);
</script>

<style scoped>
.mb-6 {
    margin-bottom: 2rem;
}

/* ── Animación ───────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
/* Este es el contenedor del título */
.section-title-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px; /* Espacio debajo del título */
}

/* Este le da el color azul y el grosor */
.section-title-bar h2 {
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-color); /* Aquí es donde toma el azul */
    margin: 0;
}
/* ── Estilos Base ─────────────────────────────────── */
.doc-section { padding: 20px; }
.description, .main-desc { font-size: 15px; opacity: 0.8; margin-bottom: 24px; line-height: 1.6; }

/* ── Layout dos columnas ──────────────────────────── */
.two-col { display: flex; gap: 32px; align-items: flex-start; margin-bottom: 32px; }
.col-steps { flex: 1; }
.col-image  { flex: 1; position: sticky; top: 20px; }

/* ── Cards y Soporte Modo Oscuro ───────────────────── */
.step-card {
    display: flex; gap: 14px; align-items: flex-start;
    background: var(--code-bg); border: 1px solid var(--border-color);
    border-radius: 10px; padding: 14px 16px; margin-bottom: 10px;
}
.step-badge {
    min-width: 28px; height: 28px; border-radius: 50%;
    background: var(--primary-color); color: #fff;
    font-size: 13px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.step-title { font-size: 14px; font-weight: 600; margin-bottom: 4px; }
.step-desc { font-size: 13px; opacity: 0.8; line-height: 1.5; }

/* ── UI Elements ─────────────────────────────────── */
.section-divider { display: flex; align-items: center; gap: 12px; margin: 32px 0 16px; }
.section-divider h2 { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.6; margin: 0; }
.divider-line { flex: 1; height: 1px; background: var(--border-color); }

/* Carrusel Box Adaptado a Modo Oscuro */
.carousel-box { 
    border: 1px solid var(--border-color); 
    padding: 8px; 
    border-radius: 10px; 
    background: var(--code-bg); 
    margin-bottom: 16px; 
}

/* ── Grid de Interpretación ────────────────────────── */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 20px 0; }
.form-grid.single-col { grid-template-columns: 1fr; }

.field-row { display: flex; gap: 10px; background: var(--code-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 12px; }
.field-key { font-weight: 700; color: var(--primary-color); }
.field-label { font-weight: 600; font-size: 13px; }
.field-desc { font-size: 12px; opacity: 0.6; }

/* ── Notas ────────────────────────────────────────── */
.doc-note { display: flex; gap: 12px; padding: 16px; border-radius: 8px; border: 1px solid; margin-top: 16px; font-size: 14px; }
.note-info { background: var(--code-bg); border-color: var(--primary-color); color: var(--text-color); }
.note-label { color: var(--primary-color); font-weight: 700; text-transform: uppercase; font-size: 11px; display: block; }

.steps-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--primary-color);
    margin-bottom: 12px;
}

/* ── Responsive ──────────────────────────────────── */
@media (max-width: 768px) {
    .two-col, .form-grid { flex-direction: column; grid-template-columns: 1fr; }
    .col-image { position: static; }
}
</style>